/*********************************************************************************
 * Copyright (C) 2025 VECROS Technologies Pvt. Ltd.
 * <legal@vecros.com>
 * This file is part of VECROS CLOUD SOFTWARE SERVICES maintained and developed by VECROS TECHNOLOGIES PRIVATE LIMITED.
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 **********************************************************************************/

import * as actionTypes from "../actionTypes";

export const authStart = () => {
  return {
    type: actionTypes.AUTH_START,
  };
};

export const authSuccess = (user) => {
  return {
    type: actionTypes.AUTH_SUCCESS,
    user,
  };
};

export const authFail = (error) => {
  return {
    type: actionTypes.AUTH_FAIL,
    error: error,
  };
};

const getAuthBaseURL = () => {
  const hostname = window.location.hostname;

  if (hostname === "localhost") {
    return "http://localhost:3000";
  } else {
    return "https://accounts.vecros.com";
  }
};

const clearAuthStorage = () => {
  localStorage.removeItem("tk");
  localStorage.removeItem("ext");
  localStorage.removeItem("usda");
  localStorage.removeItem("cst");
  localStorage.setItem("isTourShown", "true");

  const expires = "Thu, 01 Jan 1970 00:00:00 GMT";
  document.cookie = `VCS_TOKEN=; Path=/; Expires=${expires};`;
  document.cookie = `VCS_TOKEN=; Path=/; Domain=.vecros.com; Expires=${expires}; Secure; SameSite=None;`;
};

export const logout = () => {
  return async (dispatch) => {
    clearAuthStorage();

    const authBaseURL = getAuthBaseURL();
    const redirectUrl = encodeURIComponent(window.location.href);

    const iframe = document.createElement("iframe");
    iframe.style.display = "none";
    iframe.src = `${authBaseURL}/session-end`;

    const finishLogout = () => {
      if (document.body.contains(iframe)) {
        document.body.removeChild(iframe);
      }
      window.removeEventListener("message", handleMessage);
      dispatch({ type: actionTypes.AUTH_SIGNOUT });
      window.location.href = `${authBaseURL}/auth/login?redirectUrl=${redirectUrl}`;
    };

    const handleMessage = (event) => {
      if (event.data.source === "session-end") {
        finishLogout();
      }
    };

    window.addEventListener("message", handleMessage);
    document.body.appendChild(iframe);

    setTimeout(() => {
      if (document.body.contains(iframe)) {
        finishLogout();
      }
    }, 2000);
  };
};

export const endLocalSession = () => {
  return (dispatch) => {
    clearAuthStorage();
    dispatch({ type: actionTypes.AUTH_SIGNOUT });
    dispatch({ type: actionTypes.AUTH_CHECK_DONE });
  };
};

export const checkAuthTimeout = (expirationDate) => {
  return (dispatch) => {
    setTimeout(() => {
      dispatch(logout());
    }, expirationDate * 1000);
  };
};

export const getExpirationTime = () => +localStorage.getItem("ext") * 1000;
export const getExpirationDate = () => new Date(getExpirationTime());

const getCookieValue = (name) => {
  const match = document.cookie.match(new RegExp("(^| )" + name + "=([^;]+)"));
  return match ? decodeURIComponent(match[2]) : null;
};

const decodeJwtPayload = (token) => {
  try {
    const payload = token.split(".")[1];
    const normalizedPayload = payload.replace(/-/g, "+").replace(/_/g, "/");
    const decodedPayload = atob(
      normalizedPayload.padEnd(Math.ceil(normalizedPayload.length / 4) * 4, "=")
    );
    return JSON.parse(decodedPayload);
  } catch (error) {
    return null;
  }
};

const isTokenActive = (exp) => {
  const expiryInSeconds = Number(exp);
  return (
    Number.isFinite(expiryInSeconds) &&
    expiryInSeconds > Math.floor(Date.now() / 1000)
  );
};

const writeSharedTokenCookie = (token, exp) => {
  if (!token) return;

  const maxAge = Math.max(Number(exp) - Math.floor(Date.now() / 1000), 0);
  const encodedToken = encodeURIComponent(token);

  if (window.location.hostname === "localhost") {
    document.cookie = `VCS_TOKEN=${encodedToken}; Path=/; Max-Age=${maxAge}`;
    return;
  }

  document.cookie = `VCS_TOKEN=${encodedToken}; Path=/; Domain=.vecros.com; Secure; SameSite=None; Max-Age=${maxAge}`;
};

// No `orgId` here on purpose. It used to read `custom:primaryOrg`, a claim the
// Cognito pre-token trigger injected until that trigger was deleted on
// 2026-08-15; every token issued since lacks it, so the field was always null.
// The trigger is not coming back, and `custom:organisation` is not a substitute
// — it is unset for many users and, being baked in at sign-in, cannot see an org
// switch made afterwards. The workspace is server-side state: the API resolves
// it per request, and the switcher reads it from GET /developer/organisations
// (see containers/OrgSwitcher.jsx). Don't reintroduce an org claim here.
const buildUserFromToken = (token, email, exp) => {
  const claims = decodeJwtPayload(token) || {};
  const expiresAt = Number(exp || claims.exp);

  return {
    tk: token,
    username: email || claims.email || claims.name || null,
    email: email || claims.email || null,
    role: claims["custom:role"] || null,
    superuser: claims["custom:superuser"] || null,
    ext: expiresAt,
    userDetails: {
      allowedServices: {
        fleet: claims["custom:fleet"],
        inspect: claims["custom:inspect"],
        drive: claims["custom:drive"],
        workflows: claims["custom:workflows"],
        remotePilot: claims["custom:remotePilot"],
      },
      role: claims["custom:role"] || null,
      superuser: claims["custom:superuser"] || null,
      username: email || claims.email || null,
    },
  };
};

export const syncRemoteSession = ({ token, email, exp }) => {
  return (dispatch) => {
    if (!token || !isTokenActive(exp)) {
      dispatch(endLocalSession());
      return null;
    }

    const user = buildUserFromToken(token, email, exp);
    localStorage.setItem("tk", token);
    localStorage.setItem("ext", String(user.ext));
    localStorage.setItem("usda", JSON.stringify(user));
    writeSharedTokenCookie(token, user.ext);
    dispatch(authSuccess(user));
    return user;
  };
};

export const authCheckState = () => {
  return (dispatch) => {
    let token = localStorage.getItem("tk");

    // If no token in localStorage, try reading from VCS_TOKEN cookie (set by accounts app)
    if (!token) {
      const cookieToken = getCookieValue("VCS_TOKEN");
      if (cookieToken) {
        try {
          const payload = decodeJwtPayload(cookieToken);
          const exp = payload.exp;
          const nowInSeconds = Math.floor(Date.now() / 1000);

          if (exp && exp > nowInSeconds) {
            // Persist token from cookie into localStorage so the rest of the app can use it
            localStorage.setItem("tk", cookieToken);
            localStorage.setItem("ext", exp);
            token = cookieToken;
          }
        } catch (e) {
          // Failed to decode cookie token
        }
      }
    }

    if (token === null) {
      clearAuthStorage();
      dispatch({ type: actionTypes.AUTH_SIGNOUT });
      dispatch({ type: actionTypes.AUTH_CHECK_DONE });
      return;
    } else {
      const expirationDate = getExpirationDate();
      if (expirationDate <= new Date()) {
        clearAuthStorage();
        dispatch({ type: actionTypes.AUTH_SIGNOUT });
        dispatch({ type: actionTypes.AUTH_CHECK_DONE });
        return;
      } else {
        const isLocalhost = window.location.hostname === "localhost";
        if (isLocalhost) {
          document.cookie = `VCS_TOKEN=${token}; Path=/;`;
        } else {
          writeSharedTokenCookie(token, localStorage.getItem("ext"));
        }

        dispatch(
          authSuccess({
            tk: token,
            username: null,
            ext: localStorage.getItem("ext"),
            userDetails: null,
          })
        );
        dispatch(
          checkAuthTimeout(
            (expirationDate.getTime() - new Date().getTime()) / 1000
          )
        );
      }
    }
  };
};
